import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase-config';
import { Button, Card, CardContent, Typography, Modal, Box, TextField, MenuItem, Select, FormControl, InputLabel, Checkbox, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { useAuth } from '../AuthContext';
import { jsPDF } from "jspdf";
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { TrainRounded } from '@mui/icons-material';

function EventDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { userRole } = useAuth();
    const isAdmin = userRole === 'admin';
    const signupsClosed = TrainRounded;
    const [openRODO, setOpenRODO] = useState(false);
    const [openRegulations, setOpenRegulations] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [event, setEvent] = useState(null);
    const [open, setOpen] = useState(false);
    const [participantData, setParticipantData] = useState({
        eventParticipantName: '',
        eventParticipantSurname: '',
        eventParticipantGender: '',
        eventParticipantEmail: '',
        eventParticipantPhone: '',
        eventParticipantLocality: '',
        eventParticipantTeam: '',
        eventParticipantShirtSize: '',
        eventParticipantBirthDate: ''
    });

    const [consent, setConsent] = useState(false);
    const [regulationsAccepted, setRegulationsAccepted] = useState(false);
    const [errors, setErrors] = useState({});

    const handleOpenRODO = () => setOpenRODO(true);

    const handleCloseRODO = () => setOpenRODO(false);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setParticipantData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            const newErrors = { ...errors };
            delete newErrors[name];
            setErrors(newErrors);
        }
    };

    const handleConsentChange = (event) => {
        setConsent(event.target.checked);
        if (errors.consent) {
            const newErrors = { ...errors };
            delete newErrors.consent;
            setErrors(newErrors);
        }
    };
    const [sponsorModalOpen, setSponsorModalOpen] = useState(false);
    const [newSponsor, setNewSponsor] = useState({ name: '', url: '', image: '' });

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const validateForm = () => {
        const newErrors = {};
        if (event?.requiredFields?.eventParticipantName && !participantData.eventParticipantName) {
            newErrors.eventParticipantName = 'Imię jest wymagane';
        }
        if (event?.requiredFields?.eventParticipantSurname && !participantData.eventParticipantSurname) {
            newErrors.eventParticipantSurname = 'Nazwisko jest wymagane';
        }
        if (event?.requiredFields?.eventParticipantGender && !participantData.eventParticipantGender) {
            newErrors.eventParticipantGender = 'Płeć jest wymagana';
        }
        if (event?.requiredFields?.eventParticipantEmail && !participantData.eventParticipantEmail) {
            newErrors.eventParticipantEmail = 'Email jest wymagany';
        }
        if (event?.requiredFields?.eventParticipantLocality && !participantData.eventParticipantLocality) {
            newErrors.eventParticipantLocality = 'Miejscowość jest wymagana';
        }
        if (event?.requiredFields?.eventParticipantBirthDate && !participantData.eventParticipantBirthDate) {
            newErrors.eventParticipantBirthDate = 'Data urodzenia jest wymagana';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleDelete = async () => {
        if (window.confirm("Czy na pewno chcesz usunąć to wydarzenie?")) {
            await deleteDoc(doc(db, "events", id));
            navigate('/');
        }
    };

    const handleEdit = () => {
        navigate(`/edit/${id}`);
    };

    useEffect(() => {
        const fetchEvent = async () => {
            const docRef = doc(db, "events", id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setEvent(docSnap.data());
            } else {
                console.log("No such event!");
            }
        };

        fetchEvent();
    }, [id]);

    const sendConfirmationEmail = async () => {
        const url = 'https://us-central1-aktywni-stce.cloudfunctions.net/sendConfirmationEmail';
        const payload = {
            eventParticipantName: participantData.eventParticipantName,
            eventParticipantSurname: participantData.eventParticipantSurname,
            eventParticipantLocality: participantData.eventParticipantLocality,
            eventParticipantPhone: participantData.eventParticipantPhone,
            eventParticipantEmail: participantData.eventParticipantEmail,
            eventParticipantShirtSize: participantData.eventParticipantShirtSize || 'Nie podano',
            eventName: event.eventName,
            eventDate: new Date(event.eventDateTime).toLocaleDateString('pl-PL'),
            eventLocation: event.eventLocation || 'Brak miejsca startu',
            eventDescription: event.eventDescription
        };

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify(payload)
            });

            console.log('✅ FETCH RESPONSE:', response);

            if (response.ok) {
                const data = await response.text();
                console.log('✅ E-mail wysłany:', data);
            } else {
                const errorText = await response.text();
                console.error('❌ Błąd wysyłki:', response.status, errorText);
            }
        } catch (error) {
            console.error('❌ Błąd połączenia z funkcją maila:', error);
        }
    };

    const handleDeleteParticipant = async (participantIndex) => {
        if (!window.confirm("Czy na pewno chcesz usunąć tego uczestnika?")) return;

        try {
            const updatedParticipants = event.eventParticipants.filter((_, index) => index !== participantIndex);

            await updateDoc(doc(db, "events", id), {
                eventParticipants: updatedParticipants
            });

            setEvent(prev => ({
                ...prev,
                eventParticipants: updatedParticipants
            }));

            alert("Uczestnik został usunięty.");
        } catch (error) {
            console.error("Błąd podczas usuwania uczestnika:", error);
            alert("Nie udało się usunąć uczestnika.");
        }
    };

    const handleSignup = async () => {
        if (!validateForm()) {
            alert('Proszę wypełnić wszystkie wymagane pola oraz zaznaczyć zgodę!');
            return;
        }
        try {
            await updateDoc(doc(db, "events", id), {
                eventParticipants: arrayUnion(participantData)
            });
            await sendConfirmationEmail();
            alert('Zapisano pomyślnie!');
            handleClose();
            window.location.reload();
        } catch (error) {
            console.error('Błąd przy zapisie: ', error);
            alert(`Nie udało się zapisać. Szczegóły błędu: ${error.message}`);
        }
    };

    const handlePaymentStatusChange = async (participantIndex) => {
        try {
            const updatedParticipants = event.eventParticipants.map((participant, index) => {
                if (index === participantIndex) {
                    const newStatus = participant.paymentStatus === 'TAK' ? 'NIE' : 'TAK';
                    return { ...participant, paymentStatus: newStatus };
                }
                return participant;
            });

            await updateDoc(doc(db, "events", id), {
                eventParticipants: updatedParticipants
            });

            setEvent((prev) => ({
                ...prev,
                eventParticipants: updatedParticipants
            }));

            alert("Status płatności został zaktualizowany.");
        } catch (error) {
            console.error("Błąd podczas zmiany statusu płatności:", error);
            alert("Nie udało się zmienić statusu płatności.");
        }
    };


    const removePolishCharacters = (text) => {
        if (!text) return "";
        return text
            .replace(/ą/g, "a")
            .replace(/ć/g, "c")
            .replace(/ę/g, "e")
            .replace(/ł/g, "l")
            .replace(/ń/g, "n")
            .replace(/ó/g, "o")
            .replace(/ś/g, "s")
            .replace(/ź/g, "z")
            .replace(/ż/g, "z")
            .replace(/Ą/g, "A")
            .replace(/Ć/g, "C")
            .replace(/Ę/g, "E")
            .replace(/Ł/g, "L")
            .replace(/Ń/g, "N")
            .replace(/Ó/g, "O")
            .replace(/Ś/g, "S")
            .replace(/Ź/g, "Z")
            .replace(/Ż/g, "Z");
    };

    const generatePDF = () => {
        const doc = new jsPDF({
            orientation: "landscape",
            unit: "pt",
            format: "a4"
        });

        const title = `Lista uczestników wydarzenia: ${removePolishCharacters(event.eventName)}`;
        const margin = 40;
        const titleHeight = 30;

        doc.setFontSize(14);
        doc.text(title, margin, titleHeight);

        const tableColumn = [
            "l.p.",
            "nazwisko",
            "imie",
            "plec",
            "miejscowosc",
            "klub",
            "telefon",
            "e-mail",
            "rozmiar",
            "data ur.",
            "wpisowe"
        ];

        const tableRows = [];
        event.eventParticipants.forEach((participant, index) => {
            const participantDataRow = [
                index + 1,
                removePolishCharacters(participant.eventParticipantSurname || "Brak"),
                removePolishCharacters(participant.eventParticipantName || "Brak"),
                removePolishCharacters(participant.eventParticipantGender || "Brak"),
                removePolishCharacters(participant.eventParticipantLocality || "Brak"),
                removePolishCharacters(participant.eventParticipantTeam || "Brak"),
                removePolishCharacters(participant.eventParticipantPhone || "Brak"),
                removePolishCharacters(participant.eventParticipantEmail || "Brak"),
                removePolishCharacters(participant.eventParticipantShirtSize || "Brak"),
                participant.eventParticipantBirthDate || "Brak",
                "NIE"
            ];
            tableRows.push(participantDataRow);
        });

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: titleHeight + 20,
            styles: {
                font: "helvetica",
                fontSize: 8
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: [255, 255, 255],
                fontSize: 10
            },
            columnStyles: {
                0: { cellWidth: 30 },
                1: { cellWidth: 70 },
                2: { cellWidth: 70 },
                3: { cellWidth: 40 },
                4: { cellWidth: 100 },
                5: { cellWidth: 70 },
                6: { cellWidth: 70 },
                7: { cellWidth: 100 },
                8: { cellWidth: 70 },
                9: { cellWidth: 70 }
            }
        });

        doc.save(`report_${removePolishCharacters(event.eventName)}.pdf`);
    };

    const generateStartingListPDF = () => {
        if (!event || !event.eventParticipants) {
            alert('Brak uczestników do wygenerowania listy.');
            return;
        }

        const doc = new jsPDF({
            orientation: "portrait",
            unit: "pt",
            format: "a4"
        });

        const margin = 10;
        const title = `Lista startowa: ${removePolishCharacters(event.eventName)}`;
        const titleHeight = 30;

        doc.setFontSize(14);
        doc.text(title, margin, titleHeight);

        // Oddziel osoby 1-10 (tak jak są) i od 11 dalej sortuj alfabetycznie
        const firstTen = event.eventParticipants.slice(0, 10);
        const restSorted = [...event.eventParticipants.slice(10)].sort((a, b) => {
            const surnameA = (a.eventParticipantSurname || '').toLowerCase();
            const surnameB = (b.eventParticipantSurname || '').toLowerCase();
            return surnameA.localeCompare(surnameB);
        });

        const finalList = [...firstTen, ...restSorted];

        const tableRows = finalList.map((participant, index) => [
            removePolishCharacters(participant.eventParticipantName || ""),
            removePolishCharacters(participant.eventParticipantSurname || ""),
            removePolishCharacters(participant.eventParticipantLocality || ""),
            "", // Punkt kontrolny 1 (puste)
            ""  // Punkt kontrolny 2 (puste)
        ]);

        const tableColumn = ["Imię", "Nazwisko", "Miejscowość", "Punkt kontrolny 1", "Punkt kontrolny 2"];

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: titleHeight + 20,
            styles: {
                font: "helvetica",
                fontSize: 10
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: [255, 255, 255],
                fontSize: 12
            },
            margin: { top: 40 }
        });

        doc.save(`lista_startowa_${removePolishCharacters(event.eventName)}.pdf`);
    };

    const generateExcel = () => {
        if (!event || !event.eventParticipants) {
            alert("Brak uczestników do eksportu.");
            return;
        }

        const firstTen = event.eventParticipants.slice(0, 10);
        const restSorted = [...event.eventParticipants.slice(10)].sort((a, b) =>
            (a.eventParticipantSurname || '').localeCompare(b.eventParticipantSurname || '')
        );

        const allParticipants = [...firstTen, ...restSorted];

        const data = allParticipants.map((p, index) => ({
            Lp: index + 1,
            Imię: p.eventParticipantName || '',
            Nazwisko: p.eventParticipantSurname || '',
            Płeć: p.eventParticipantGender || '',
            Email: p.eventParticipantEmail || '',
            Telefon: p.eventParticipantPhone || '',
            Miejscowość: p.eventParticipantLocality || '',
            Klub: p.eventParticipantTeam || '',
            Rozmiar_koszulki: p.eventParticipantShirtSize || '',
            Data_urodzenia: p.eventParticipantBirthDate || '',
            Wpisowe: p.paymentStatus || 'NIE'
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Uczestnicy");

        const filename = `uczestnicy_${removePolishCharacters(event.eventName)}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };


    const eventDate = event?.eventDateTime ? new Date(event.eventDateTime).toLocaleString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }) : 'Brak ustalonej daty.';
    const mapsUrl = event?.eventLocation
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.eventLocation)}`
        : null;

    if (!event) return <div>Ładowanie...</div>;

    return (
        <Card sx={{ width: '90%', m: "auto", mt: 5, boxShadow: 3, bgcolor: 'background.paper' }}>
            <CardContent>
                <Box
                    sx={{
                        p: 3,
                        bgcolor: '#f0f4f8',
                        borderRadius: '8px',
                        border: '1px solid #cfd8dc',
                        boxShadow: 2,
                        textAlign: 'left',
                        mb: 3
                    }}
                >
                    <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: '#2c3e50', textAlign: 'center' }}>
                        {event.eventName}
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                        <strong>📅 Data i czas:</strong> {eventDate}
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                        <strong>📍 Miejsce startu:</strong>{' '}
                        {event.eventLocation ? (
                            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                                {event.eventLocation}
                            </a>
                        ) : (
                            'Brak ustalonego miejsca.'
                        )}
                    </Typography>

                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: '#555', mb: 2 }}>
                        {event.eventDescription}
                    </Typography>

                    {event.isPaid && (
                        <>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                                💳 Dane do przelewu:
                            </Typography>
                            <Typography><strong>Odbiorca:</strong> Klub Aktywnej Turystyki Pieszej Aktywni Starachowice</Typography>
                            <Typography><strong>Nr konta:</strong> 81 1020 2674 0000 2802 0209 0652</Typography>
                            <Typography>
                                <strong>Tytuł przelewu:</strong>{' '}
                                {`${event.eventName || 'Nazwa wydarzenia'} ${participantData.eventParticipantName || 'imię'} ${participantData.eventParticipantSurname || 'nazwisko'}`}
                            </Typography>
                            <Typography sx={{ mt: 2 }}><strong>Uwaga:</strong> Opłata jest obowiązkowa przed wydarzeniem.</Typography>
                        </>
                    )}
                    {event.isSponsored && (
                        <>
                            {/* SPONSORZY */}
                            {event.eventSponsors && event.eventSponsors.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                                        🤝 Sponsorzy:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                                        {event.eventSponsors.map((sponsor, index) => (
                                            <Box key={index} sx={{ textAlign: 'center' }}>
                                                <a href={sponsor.url} target="_blank" rel="noopener noreferrer">
                                                    <img src={sponsor.image} alt={sponsor.name} style={{ maxWidth: '200px', height: 'auto' }} />
                                                </a>
                                                <Typography variant="body2">{sponsor.name}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            )}

                            {/* PATRONI MEDIALNI */}
                            {event.eventMediaPatrons && event.eventMediaPatrons.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                                        🎤 Patronat medialny:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                                        {event.eventMediaPatrons.map((media, index) => (
                                            <Box key={index} sx={{ textAlign: 'center' }}>
                                                <a href={media.url} target="_blank" rel="noopener noreferrer">
                                                    <img src={media.image} alt={media.name} style={{ maxWidth: '150px', height: 'auto' }} />
                                                </a>
                                                <Typography variant="body2">{media.name}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            )}

                            {/* PATRONI HONOROWI */}
                            {event.eventHonorPatrons && event.eventHonorPatrons.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                                        🎖 Patronat honorowy:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                                        {event.eventHonorPatrons.map((honor, index) => (
                                            <Box key={index} sx={{ textAlign: 'center' }}>
                                                <a href={honor.url} target="_blank" rel="noopener noreferrer">
                                                    <img src={honor.image} alt={honor.name} style={{ maxWidth: '150px', height: 'auto' }} />
                                                </a>
                                                <Typography variant="body2">{honor.name}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            )}
                        </>
                    )}
                </Box>
                {signupsClosed ? (
                    <Typography sx={{ textAlign: 'center', mt: 3, fontWeight: 'bold', color: 'error.main', fontSize: '1.2rem' }}>
                        Zapisy zostały już zakończone. Zapraszamy do udziału w przyszłym roku :)
                    </Typography>
                ) : event.eventParticipants?.length < 250 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button onClick={handleOpen} variant="contained" color="primary" sx={{ minWidth: 150, minHeight: 30 }}>
                            Zapisz się!
                        </Button>
                    </Box>
                ) : (
                    <Typography sx={{ textAlign: 'center', mt: 3, fontWeight: 'bold', color: 'error.main' }}>
                        Limit 250 osób został osiągnięty 🙂
                    </Typography>
                )}
                {isAdmin && (
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        justifyContent: { xs: 'center', sm: 'space-around' },
                        mt: 2
                    }}>
                        {isAdmin && event.isSponsored && (
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                                <Button variant="outlined" onClick={() => setSponsorModalOpen('sponsor')}>Dodaj sponsora</Button>
                                <Button variant="outlined" onClick={() => setSponsorModalOpen('media')}>Dodaj patrona medialnego</Button>
                                <Button variant="outlined" onClick={() => setSponsorModalOpen('honor')}>Dodaj patrona honorowego</Button>
                            </Box>
                        )}
                        <Button onClick={handleEdit} color="primary" variant="contained" sx={{ minWidth: 150, minHeight: 30 }}>
                            Edytuj wydarzenie
                        </Button>
                        <Button onClick={handleDelete} color="error" variant="contained" sx={{ minWidth: 150, minHeight: 30 }}>
                            Usuń wydarzenie
                        </Button>
                        <Button onClick={generatePDF} color="secondary" variant="contained" sx={{ minWidth: 150, minHeight: 30 }}>
                            Pobierz listę
                        </Button>
                        <Button onClick={generateExcel} color="secondary" variant="contained" sx={{ minWidth: 150, minHeight: 30 }}>
                            Eksportuj do Excela
                        </Button>
                    </Box>
                )}
            </CardContent>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90vw', sm: '500px' },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    overflowY: 'auto',
                    maxHeight: '90vh'
                }}>
                    <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ textAlign: 'center' }}>
                        Wprowadź swoje dane:
                    </Typography>

                    {event?.requiredFields?.eventParticipantName && (
                        <TextField name="eventParticipantName" label="Imię" variant="outlined" fullWidth sx={{ mb: 2 }}
                            onChange={handleInputChange} error={!!errors.eventParticipantName} helperText={errors.eventParticipantName} />
                    )}

                    {event?.requiredFields?.eventParticipantSurname && (
                        <TextField name="eventParticipantSurname" label="Nazwisko" variant="outlined" fullWidth sx={{ mb: 2 }}
                            onChange={handleInputChange} error={!!errors.eventParticipantSurname} helperText={errors.eventParticipantSurname} />
                    )}

                    {event?.requiredFields?.eventParticipantGender && (
                        <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.eventParticipantGender}>
                            <InputLabel id="participant-gender-label">Płeć</InputLabel>
                            <Select
                                labelId="participant-gender-label"
                                id="participant-gender-select"
                                name="eventParticipantGender"
                                value={participantData.eventParticipantGender}
                                label="Płeć"
                                onChange={handleInputChange}
                            >
                                <MenuItem value="M">Mężczyzna</MenuItem>
                                <MenuItem value="K">Kobieta</MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    {event?.requiredFields?.eventParticipantEmail && (
                        <TextField name="eventParticipantEmail" label="Adres e-mail" variant="outlined" fullWidth sx={{ mb: 2 }}
                            onChange={handleInputChange} error={!!errors.eventParticipantEmail} helperText={errors.eventParticipantEmail} />
                    )}

                    {event?.requiredFields?.eventParticipantPhone && (
                        <TextField name="eventParticipantPhone" label="Numer telefonu" variant="outlined" fullWidth sx={{ mb: 2 }}
                            onChange={handleInputChange} />
                    )}

                    {event?.requiredFields?.eventParticipantLocality && (
                        <TextField name="eventParticipantLocality" label="Miejscowość" variant="outlined" fullWidth sx={{ mb: 2 }}
                            onChange={handleInputChange} error={!!errors.eventParticipantLocality} helperText={errors.eventParticipantLocality} />
                    )}

                    {event?.requiredFields?.eventParticipantBirthDate && (
                        <TextField
                            name="eventParticipantBirthDate"
                            label="Data urodzenia"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            fullWidth
                            sx={{ mb: 2 }}
                            onChange={handleInputChange}
                            value={participantData.eventParticipantBirthDate}
                            error={!!errors.eventParticipantBirthDate}
                            helperText={errors.eventParticipantBirthDate}
                        />
                    )}

                    {event?.requiredFields?.eventParticipantTeam && (
                        <TextField name="eventParticipantTeam" label="Klub (drużyna)" variant="outlined" fullWidth sx={{ mb: 2 }}
                            onChange={handleInputChange} />
                    )}

                    {event?.requiredFields?.eventParticipantShirtSize && (
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id="participant-shirt-label">Rozmiar koszulki</InputLabel>
                            <Select
                                labelId="participant-shirt-label"
                                id="participant-shirt-select"
                                name="eventParticipantShirtSize"
                                value={participantData.eventParticipantShirtSize || ''}
                                label="Rozmiar koszulki"
                                onChange={handleInputChange}
                            >
                                <MenuItem value="S">S</MenuItem>
                                <MenuItem value="M">M</MenuItem>
                                <MenuItem value="L">L</MenuItem>
                                <MenuItem value="XL">XL</MenuItem>
                                <MenuItem value="XXL">XXL</MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    <FormControlLabel
                        control={<Checkbox checked={consent} onChange={handleConsentChange} name="consent" />}
                        label={
                            <>
                                Wyrażam zgodę na przetwarzanie moich danych osobowych w celach organizacji wydarzenia.
                                <Button onClick={handleOpenRODO} variant="text" color="primary" size="small">Szczegóły</Button>
                            </>
                        }
                        sx={{ mb: 2 }}
                    />

                    {event?.hasRegulations && (
                        <FormControlLabel
                            control={<Checkbox checked={regulationsAccepted} onChange={(e) => setRegulationsAccepted(e.target.checked)} />}
                            label={
                                <>
                                    Akceptuję&nbsp;
                                    <Button onClick={() => setOpenRegulations(true)} variant="text" color="primary" size="small">
                                        regulamin wydarzenia
                                    </Button>
                                </>
                            }
                            sx={{ mb: 2 }}
                        />
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button onClick={handleSignup} variant="contained" color="primary">
                            Zapisz się!
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal
                open={openRODO}
                onClose={handleCloseRODO}
                aria-labelledby="rodo-modal-title"
                aria-describedby="rodo-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90vw', sm: '500px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    overflowY: 'auto',
                    borderRadius: 2
                }}>
                    <Typography id="rodo-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        Klauzula informacyjna RODO
                    </Typography>
                    <Typography id="rodo-modal-description" variant="body2" sx={{ textAlign: 'justify' }}>
                        Zgodnie z art. 13 ust. 1 i ust. 2 Rozporządzenia Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r. (zwanego dalej „RODO”), informujemy, że:
                        <br /><br />
                        <strong>1. Administrator danych osobowych:</strong><br />
                        Administratorem Państwa danych osobowych jest "KLUB AKTYWNEJ TURYSTYKI PISZEJ - AKTYWNI STARACHOWICE", z siedzibą pod adresem: ul. Piotra Wysockiego 4/19, 27-200, Starachowice, wpisane do rejestru stowarzyszeń zwykłych.
                        <br /><br />
                        <strong>2. Cele przetwarzania danych osobowych:</strong><br />
                        Dane osobowe przetwarzane są w celu organizacji i realizacji wycieczek pieszych oraz promocji zdrowego stylu życia i turystyki pieszej.
                        <br /><br />
                        <strong>3. Podstawa prawna przetwarzania:</strong><br />
                        - Art. 6 ust. 1 lit. a RODO – zgoda osoby, której dane dotyczą,<br />
                        - Art. 6 ust. 1 lit. b RODO – niezbędność do wykonania umowy.<br /><br />
                        <strong>4. Prawa osób, których dane dotyczą:</strong><br />
                        Mają Państwo prawo do dostępu do danych, ich sprostowania, usunięcia, ograniczenia przetwarzania, wniesienia sprzeciwu oraz cofnięcia zgody. Szczegóły można znaleźć w pełnym dokumencie.
                        <br /><br />
                        <strong>5. Inne informacje:</strong><br />
                        Pełna treść klauzuli dostępna jest w siedzibie stowarzyszenia lub na żądanie pod adresem kontaktowym.
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <Button
                            onClick={handleCloseRODO}
                            variant="contained"
                            color="primary"
                            sx={{ minWidth: 100 }}
                        >
                            Ok
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <Modal
                open={openRegulations}
                onClose={() => setOpenRegulations(false)}
                aria-labelledby="regulations-modal-title"
                aria-describedby="regulations-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90vw', sm: '600px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    overflowY: 'auto',
                    borderRadius: 2
                }}>

                    <Typography id="regulations-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        Regulamin Maratonu
                    </Typography>
                    <Box sx={{ maxHeight: '60vh', overflowY: 'auto', p: 2, border: '1px solid #ccc', borderRadius: '8px' }}>
                        <Typography id="regulations-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                            Regulamin wydarzenia
                        </Typography>

                        <Typography id="regulations-modal-description" variant="body2" sx={{ textAlign: 'justify', whiteSpace: 'pre-line' }}>
                            {event?.regulationsText || 'Brak treści regulaminu.'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Button
                            onClick={() => setOpenRegulations(false)}
                            variant="contained"
                            color="primary"
                            sx={{ minWidth: 100 }}
                        >
                            Zamknij
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <Box sx={{ mt: 3, mb: 2 }}>
                <TextField
                    label="Szukaj uczestnika (imię, nazwisko, miejscowość)"
                    variant="outlined"
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </Box>
            {event.eventParticipants && event.eventParticipants.length > 0 && (
                <TableContainer component={Paper} sx={{ mt: 3 }}>
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>L.p.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Nazwisko</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Imię</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Płeć</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Miejscowość</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Klub (drużyna)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Wpisowe (czy opłacono)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {event.eventParticipants
                                .filter((participant) =>
                                    `${participant.eventParticipantName} ${participant.eventParticipantSurname} ${participant.eventParticipantLocality}`
                                        .toLowerCase()
                                        .includes(searchQuery.toLowerCase())
                                )
                                .map((participant, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            bgcolor: participant.paymentStatus === 'TAK' ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)'
                                        }}
                                    >
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell component="th" scope="row">{participant.eventParticipantSurname}</TableCell>
                                        <TableCell>{participant.eventParticipantName}</TableCell>
                                        <TableCell>{participant.eventParticipantGender}</TableCell>
                                        <TableCell>{participant.eventParticipantLocality}</TableCell>
                                        <TableCell>{participant.eventParticipantTeam}</TableCell>
                                        <TableCell>
                                            {participant.paymentStatus || 'NIE'}
                                            {isAdmin && (
                                                <>
                                                    <Button
                                                        onClick={() => handlePaymentStatusChange(index)}
                                                        variant="contained"
                                                        color={participant.paymentStatus === 'TAK' ? 'error' : 'primary'}
                                                        size="small"
                                                        sx={{ ml: 1 }}
                                                    >
                                                        {participant.paymentStatus === 'TAK' ? 'Ustaw NIE' : 'Ustaw TAK'}
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleDeleteParticipant(index)}
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        sx={{ ml: 1 }}
                                                    >
                                                        Usuń
                                                    </Button>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {event.eventParticipants.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">Brak zapisanych uczestników</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <Modal open={!!sponsorModalOpen} onClose={() => setSponsorModalOpen(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400, bgcolor: 'background.paper', p: 4, borderRadius: 2
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {sponsorModalOpen === 'sponsor' && 'Dodaj sponsora'}
                        {sponsorModalOpen === 'media' && 'Dodaj patrona medialnego'}
                        {sponsorModalOpen === 'honor' && 'Dodaj patrona honorowego'}
                    </Typography>

                    <TextField label="Nazwa" fullWidth sx={{ mb: 2 }} value={newSponsor.name}
                        onChange={(e) => setNewSponsor(prev => ({ ...prev, name: e.target.value }))} />
                    <TextField label="Link do strony" fullWidth sx={{ mb: 2 }} value={newSponsor.url}
                        onChange={(e) => setNewSponsor(prev => ({ ...prev, url: e.target.value }))} />
                    <TextField label="Link do obrazka (logo)" fullWidth sx={{ mb: 2 }} value={newSponsor.image}
                        onChange={(e) => setNewSponsor(prev => ({ ...prev, image: e.target.value }))} />

                    <Button variant="contained" fullWidth onClick={async () => {
                        const field = sponsorModalOpen === 'sponsor' ? 'eventSponsors' :
                            sponsorModalOpen === 'media' ? 'eventMediaPatrons' :
                                'eventHonorPatrons';
                        try {
                            await updateDoc(doc(db, "events", id), {
                                [field]: arrayUnion(newSponsor)
                            });
                            alert('Dodano pomyślnie!');
                            setNewSponsor({ name: '', url: '', image: '' });
                            setSponsorModalOpen(false);
                            window.location.reload(); // lub odświeżenie eventu bez reload
                        } catch (error) {
                            console.error('Błąd przy dodawaniu sponsora/patrona:', error);
                            alert('Coś poszło nie tak');
                        }
                    }}>
                        Dodaj
                    </Button>
                </Box>
            </Modal>
        </Card>
    );
}

export default EventDetails;
